import { ArrowUpRight, CreditCard, Mail, FileText, Check, X } from "lucide-react";
import { HeroFade, Reveal } from "../components/Motion";
import { MarqueeStrip } from "../components/MarqueeStrip";
import { STRIPE_EMAIL_CHECK_URL, EMAIL } from "../constants";

const STEPS = [
  {
    num: "01",
    icon: CreditCard,
    title: "Pay $49",
    body: "Pay $49 on this page (Stripe, card).",
  },
  {
    num: "02",
    icon: Mail,
    title: "Send Your Domain",
    body: `After payment, email ${EMAIL} with the domain (example: yourcompany.com) if we do not already have it.`,
  },
  {
    num: "03",
    icon: FileText,
    title: "Get The Report",
    body: "We send the written report in 1 business day.",
  },
];

const YOU_GET = [
  {
    title: "A written English report",
    body: "For one domain — its public mail records, read by a human.",
    large: true,
  },
  { title: "What is missing", body: "What is missing on the public mail records." },
  { title: "Exact lines to paste", body: "The exact lines to paste, and where." },
  { title: "1 business day", body: "Delivery in 1 business day after payment. You keep the report." },
];

const YOU_DONT_GET = [
  "We do not log in to your accounts.",
  "We do not change DNS or email settings for you.",
  "We do not promise Gmail or Outlook will never spam you again.",
  "We do not certify anything. This is not legal advice.",
];

export default function EmailCheck() {
  return (
    <main data-testid="email-check-page" className="pb-28">
      <section className="relative overflow-hidden px-6 pt-36 md:px-10 md:pt-44">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-blue-100/60 blur-3xl" />
        <div className="relative mx-auto max-w-7xl pb-20">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
            <div>
              <HeroFade delay={0.1}>
                <p className="mb-6 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
                  EY TECH LLC — Email Delivery Check
                </p>
              </HeroFade>
              <h1 data-testid="email-check-heading" className="font-serif text-4xl font-medium leading-[1.1] tracking-tight text-slate-900 md:text-6xl">
                <HeroFade delay={0.2}>The $49 Email</HeroFade>
                <HeroFade delay={0.35}>
                  Delivery <em className="text-blue-600">Check.</em>
                </HeroFade>
              </h1>
              <HeroFade delay={0.5}>
                <p data-testid="email-check-lead" className="mt-10 max-w-xl text-base leading-relaxed text-slate-700 md:text-lg">
                  If you send quotes, invoices, or policy papers from your own domain,
                  some of them may never arrive. Gmail and Outlook now bounce or bury
                  mail from companies whose public mail records are incomplete. The
                  other side often never tells you.
                </p>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-700 md:text-lg">
                  We do not log in. We do not change anything. We look at the public
                  records for your domain and send a written report of what is missing
                  and what to paste. You or your IT person do the paste. $49, card,
                  1 business day. The full list comes after payment.
                </p>
              </HeroFade>
            </div>
            <HeroFade delay={0.65}>
              <div className="rounded-2xl bg-white p-8 shadow-lift md:p-10">
                <div className="mb-6 flex items-end justify-between">
                  <span className="font-serif text-6xl font-medium tracking-tight text-slate-900 md:text-7xl">
                    $49
                  </span>
                  <span className="rounded-full bg-blue-50 px-4 py-1.5 text-xs font-semibold text-blue-700">
                    Flat / Once
                  </span>
                </div>
                <ul className="mb-8 space-y-2.5 text-sm font-medium text-slate-600">
                  <li data-testid="price-fact-card" className="flex items-center gap-2.5">
                    <Check className="h-4 w-4 text-blue-600" /> Card — via Stripe
                  </li>
                  <li data-testid="price-fact-delivery" className="flex items-center gap-2.5">
                    <Check className="h-4 w-4 text-blue-600" /> 1 business day
                  </li>
                  <li data-testid="price-fact-list" className="flex items-center gap-2.5">
                    <Check className="h-4 w-4 text-blue-600" /> Full list after payment
                  </li>
                </ul>
                <a
                  href={STRIPE_EMAIL_CHECK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pay-stripe-button"
                  className="group flex items-center justify-center gap-2.5 rounded-full bg-blue-600 px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-md"
                >
                  Pay $49 — Email Delivery Check
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <p className="mt-4 text-xs leading-relaxed text-slate-400">
                  Payment processed by Stripe. After you pay, email us the domain if we do not already have it.
                </p>
              </div>
            </HeroFade>
          </div>
        </div>
      </section>

      <MarqueeStrip />

      <section className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
        <Reveal>
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
            Process
          </p>
          <h2 data-testid="how-it-works-heading" className="mb-16 font-serif text-3xl font-medium leading-[1.15] tracking-tight text-slate-900 md:text-5xl">
            How it works.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.1}>
              <article
                data-testid={`step-${s.num}`}
                className="group flex h-full flex-col gap-6 rounded-2xl bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift md:p-10"
              >
                <div className="flex items-center justify-between">
                  <span className="font-serif text-4xl font-medium italic text-slate-300 transition-colors duration-300 group-hover:text-blue-600">
                    {s.num}
                  </span>
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 transition-colors duration-300 group-hover:bg-blue-100">
                    <s.icon className="h-5 w-5 text-blue-600" />
                  </span>
                </div>
                <h3 className="font-serif text-2xl font-medium tracking-tight text-slate-900">
                  {s.title}
                </h3>
                <p className="text-sm leading-relaxed text-slate-600">{s.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-2">
          <div>
            <Reveal>
              <h2 data-testid="you-get-heading" className="mb-12 font-serif text-3xl font-medium tracking-tight text-slate-900 md:text-5xl">
                What you <em className="text-blue-600">get.</em>
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {YOU_GET.map((item, i) => (
                <Reveal
                  key={item.title}
                  delay={i * 0.08}
                  className={item.large ? "sm:col-span-2" : ""}
                >
                  <div
                    data-testid={`you-get-item-${i}`}
                    className="flex h-full flex-col gap-4 rounded-2xl bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50">
                      <Check className="h-4 w-4 text-blue-600" />
                    </span>
                    <h3 className={`font-serif font-medium tracking-tight text-slate-900 ${item.large ? "text-2xl md:text-3xl" : "text-xl"}`}>
                      {item.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-slate-600">{item.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div>
            <Reveal>
              <h2 data-testid="you-dont-get-heading" className="mb-12 font-serif text-3xl font-medium tracking-tight text-slate-400 md:text-5xl">
                What you don't get.
              </h2>
            </Reveal>
            <ul className="flex flex-col gap-4">
              {YOU_DONT_GET.map((item, i) => (
                <Reveal key={item} delay={i * 0.08}>
                  <li
                    data-testid={`you-dont-get-item-${i}`}
                    className="flex items-center gap-4 rounded-2xl bg-slate-50 px-6 py-5 transition-colors duration-300 hover:bg-slate-100"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white shadow-soft">
                      <X className="h-4 w-4 text-slate-400" />
                    </span>
                    <span className="text-sm leading-relaxed text-slate-600 md:text-base">
                      {item}
                    </span>
                  </li>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={0.4}>
              <p className="mt-8 text-sm font-medium text-slate-500">
                Straight terms. No fine print. That is the whole product.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200/70 bg-cream/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4 md:px-10">
          <div className="hidden items-baseline gap-3 md:flex">
            <span className="font-serif text-2xl font-medium text-slate-900">$49</span>
            <span className="text-sm text-slate-500">
              Report in 1 business day
            </span>
          </div>
          <a
            href={STRIPE_EMAIL_CHECK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sticky-pay-button"
            className="group flex w-full items-center justify-center gap-2.5 rounded-full bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-md md:w-auto"
          >
            Pay $49 via Stripe
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </main>
  );
}
